import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useContent } from "../hooks/useContent";
import { ChevronLeft, Heart, BookOpen, Save } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import T from "../components/T";
import { lightTap, successPulse } from "../lib/haptics";

export default function BeforeYouGoBack() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { BEFORE_YOU_GO_BACK } = useContent();
  const [answers, setAnswers] = useState(Array(BEFORE_YOU_GO_BACK.length).fill(""));
  const [busy, setBusy] = useState(false);

  const set = (i, v) => setAnswers((a) => a.map((x, idx) => (idx === i ? v : x)));

  const saveOne = async (i) => {
    const body = answers[i].trim();
    if (!body) return toast.info(t("bygb.save_one_first"));
    setBusy(true);
    try {
      await api.post("/reflections", {
        kind: "before_you_go_back",
        prompt: BEFORE_YOU_GO_BACK[i].q,
        body,
        title: BEFORE_YOU_GO_BACK[i].q,
      });
      toast.success(t("bygb.saved_softly"));
      lightTap();
    } catch {
      toast.error(t("bygb.save_error_one"));
    } finally { setBusy(false); }
  };

  const saveAll = async () => {
    const filled = answers
      .map((a, i) => ({ i, body: a.trim() }))
      .filter((x) => x.body.length > 0);
    if (filled.length === 0) return toast.info(t("bygb.save_one_first"));
    setBusy(true);
    try {
      for (const { i, body } of filled) {
        await api.post("/reflections", {
          kind: "before_you_go_back",
          prompt: BEFORE_YOU_GO_BACK[i].q,
          body,
          title: BEFORE_YOU_GO_BACK[i].q,
        });
      }
      const key = filled.length === 1 ? "bygb.saved_n" : "bygb.saved_n_plural";
      toast.success(t(key, { n: filled.length }));
      setAnswers(Array(BEFORE_YOU_GO_BACK.length).fill(""));
      // Bulk save = a small completion. One stronger pulse, not N taps.
      successPulse();
    } catch {
      toast.error(t("bygb.save_error_all"));
    } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-before-go-back">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-script text-2xl text-brand">{t("bygb.kicker")}</p>
            <h1 className="font-serif text-4xl leading-tight">
              <T i18nKey="bygb.h" />
            </h1>
          </div>
          <button
            data-testid="bygb-history-btn"
            onClick={() => navigate("/reflections")}
            className="shrink-0 mt-2 inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-card border border-border hover:border-brand/50"
          >
            <BookOpen className="w-3.5 h-3.5" /> {t("bygb.past_reflections")}
          </button>
        </div>
        <p className="mt-3 text-foreground/70 leading-relaxed">
          <T i18nKey="bygb.intro" />
        </p>
      </div>

      <div className="px-8 space-y-5 pb-32">
        {BEFORE_YOU_GO_BACK.map((q, i) => (
          <div key={i} className="rounded-3xl bg-card border border-border p-5" data-testid={`bygb-q-${i}`}>
            <p className="font-serif text-xl">{q.q}</p>
            <p className="text-sm text-foreground/60 mt-1">{q.help}</p>
            <Textarea
              value={answers[i]}
              onChange={(e) => set(i, e.target.value)}
              placeholder={t("bygb.write_softly")}
              className="mt-3 min-h-24 rounded-2xl bg-background/60"
              data-testid={`bygb-answer-${i}`}
            />
            <div className="flex justify-end mt-2">
              <button
                type="button"
                data-testid={`bygb-save-${i}`}
                onClick={() => saveOne(i)}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-xs text-foreground/65 hover:text-brand px-3 py-1 disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" /> {t("bygb.save_one")}
              </button>
            </div>
          </div>
        ))}

        <Button
          data-testid="bygb-save-all-btn"
          onClick={saveAll}
          disabled={busy}
          className="w-full h-13 rounded-full bg-brand hover:bg-brand/90 text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {busy ? t("common.saving") : t("bygb.save_all")}
        </Button>

        <div className="rounded-3xl bg-brand/8 border border-brand/20 p-6 flex items-start gap-3">
          <Heart className="w-5 h-5 text-brand mt-1 shrink-0" />
          <p className="font-serif italic text-foreground/85 leading-relaxed">
            {t("bygb.footer_quote")}
          </p>
        </div>
      </div>
    </AppShell>
  );
}
